import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Download, Mail, Phone, Building2, Settings2, Compass, CloudSun, Layers, GraduationCap, BadgeCheck, Sparkles, Heart, Wrench, BrainCircuit, MoreHorizontal, Pencil, Power, Trash2, Archive, ShieldAlert } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import NewClientDialog, { USER_LIMIT_TIER_OPTIONS } from '@/components/admin/NewClientDialog';
import ClientRequestDialog from '@/components/admin/ClientRequestDialog';
import SuiteRequestDialog from '@/components/admin/SuiteRequestDialog';
import ConfirmDeleteDialog from '@/components/shared/ConfirmDeleteDialog';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { ListGridToggle } from '@/components/shared/ListGridToggle';

const TIER_LABEL = Object.fromEntries(USER_LIMIT_TIER_OPTIONS.map((o) => [o.code, o]));

const SUITE_ICONS = {
  talentmap: Compass, climara: CloudSun, talentcompetency: Layers, talenttrain: GraduationCap,
  talentcheck: BadgeCheck, talentstart: Sparkles, talentheart: Heart, talentfix: Wrench,
  intelligence: BrainCircuit,
};

export default function AdminClients() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Sprint Users-Nav — pending governance indicator (icon + tooltip, no column).
  const GovernanceDot = ({ c }) => {
    const n = c.pending_config_requests || 0;
    if (!n) return null;
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid={`client-governance-dot-${c.id}`}
              className="inline-flex items-center text-warning hover:opacity-70 transition-opacity shrink-0"
              onClick={(e) => { e.stopPropagation(); navigate(`/admin/leads?type=config_change&status=pending&company_id=${c.id}`); }}
            >
              <ShieldAlert className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {t('pages.clients.governancePendingTooltip', { defaultValue: 'Configuration changes awaiting HM Admin approval' })} ({n})
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const { user } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  // Iter 41.10 — RBAC hardening: hm_advisor is ALWAYS book-scoped.
  // No "View all" escape; the toggle has been removed.
  const isConsultant = user?.role === 'hm_advisor';
  const showAll = false;
  const [companies, setCompanies] = useState([]);
  const [allSuites, setAllSuites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manage suites modal state
  const [manageOpen, setManageOpen] = useState(false);
  const [manageTarget, setManageTarget] = useState(null);
  const [draftSuites, setDraftSuites] = useState([]);
  const [saving, setSaving] = useState(false);

  // New client dialog state (BUG 01) — for hm_admin: direct create.
  // For hm_advisor: opens the request flow (Sesión B WF1).
  const [newOpen, setNewOpen] = useState(false);
  const [clientReqOpen, setClientReqOpen] = useState(false);
  const [suiteReqOpen, setSuiteReqOpen] = useState(false);
  // UAT Sprint 01 · Finding 4 — Edit / deactivate / delete client
  const [deleteTarget, setDeleteTarget] = useState(null);
  const isAdmin = user?.role === 'hm_admin';
  const toggleClientStatus = async (c) => {
    const next = (c.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await adminApi.updateClient(c.id, { status: next });
      toast.success(next === 'active'
        ? t('pages.clients.activated', { defaultValue: 'Client activated' })
        : t('pages.clients.deactivated', { defaultValue: 'Client deactivated' }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteClient = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.deleteClient(deleteTarget.id);
      toast.success(t('pages.clients.deleted', { defaultValue: 'Client deleted' }));
      setDeleteTarget(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  // Sprint Governance — reversible archive (Archive Service); the advisor
  // replacement for hard delete, also preferred for admins.
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const archiveClient = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await adminApi.archiveClient(archiveTarget.id);
      toast.success(t('governance.archivedToast', { defaultValue: 'Client archived — restorable from the Trash workspace' }));
      setArchiveTarget(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setArchiving(false); }
  };
  const openNewClient = () => {
    if (user?.role === 'hm_advisor') setClientReqOpen(true);
    else setNewOpen(true);
  };

  // Step B+C — grid/list toggle + sort
  const [view, setView] = useState('grid');
  const sort = useSort();

  const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';

  const reload = async () => {
    setLoading(true);
    try {
      const [cos, suites] = await Promise.all([
        // Iter 40 — backend now scopes by client book for hm_advisor; the
        // "View all clients" toggle opts the advisor into the full list.
        adminApi.listCompanies(isConsultant && showAll ? { book: 'all' } : {}),
        adminApi.listAllSuites(),
      ]);
      setCompanies(cos);
      setAllSuites(suites);
    } catch {
      toast.error(t('clientProfile.loadError', { defaultValue: 'Failed to load clients' }));
    } finally { setLoading(false); }
  };
  // Refetch whenever the consultant toggles between "My clients" / "View all".
  // (For non-advisors the dependency stays constant, so this runs once.)
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [isConsultant, showAll]);

  const filtered = useMemo(() => {
    return (companies || [])
      .filter((c) => statusFilter === 'all' || (c.status || 'active') === statusFilter)
      .filter((c) => {
        const needle = (q || '').toLowerCase();
        if (!needle) return true;
        return [c.name, c.sector, c.country].filter(Boolean)
          .some(s => s.toLowerCase().includes(needle));
      });
  }, [companies, q, statusFilter]);

  const sorted = useMemo(() => sort.apply(filtered, {
    name:     (c) => (c.name || '').toLowerCase(),
    industry: (c) => (c.sector || '').toLowerCase(),
    employees:(c) => c.employees || 0,
    suites:   (c) => (c.suite_codes || []).length,
    tier:     (c) => (c.user_limit_tier || 'up_to_25'),
    status:   (c) => (c.status || 'active'),
  }), [filtered, sort]);

  const tierLabel = (code) => {
    const o = TIER_LABEL[code || 'up_to_25'];
    if (!o) return code || '—';
    return i18n.language?.startsWith('es') ? o.label_es : o.label_en;
  };

  const updateStatus = (v) => {
    setStatusFilter(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'all') next.delete('status'); else next.set('status', v);
    setSearchParams(next, { replace: true });
  };

  const openManage = (company) => {
    // hm_advisor → opens the suite-activation request flow (Sesión B WF2).
    if (user?.role === 'hm_advisor') {
      setManageTarget(company);
      setSuiteReqOpen(true);
      return;
    }
    setManageTarget(company);
    setDraftSuites(company.suite_codes || []);
    setManageOpen(true);
  };

  const toggleSuite = (code) => {
    setDraftSuites((prev) => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const saveSuites = async () => {
    if (!manageTarget) return;
    setSaving(true);
    try {
      const res = await adminApi.updateClientSuites(manageTarget.id, draftSuites);
      toast.success(t('clientProfile.suitesSaved', { defaultValue: 'Suites updated' }));
      setCompanies((prev) => prev.map(c => c.id === manageTarget.id ? res.company : c));
      setManageOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Topbar
        title={t('pages.clients.title')}
        subtitle={t('pages.clients.subtitle')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="clients-export-btn" className="hidden md:inline-flex gap-1.5">
              <Download className="h-4 w-4" /> {t('pages.clients.export')}
            </Button>
            <Button size="sm" data-testid="new-client-trigger" onClick={openNewClient} className="bg-gradient-primary hover:opacity-90 gap-1.5">
              <Plus className="h-4 w-4" /> {t('pages.clients.newClient')}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-5">
        <Card className="p-4 shadow-card flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="clients-search"
              placeholder={t('pages.clients.searchPh')}
              value={q}
              onChange={e=>setQ(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={updateStatus}>
            <SelectTrigger data-testid="clients-status-filter" className="w-36 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pages.clients.allStatus')}</SelectItem>
              <SelectItem value="active">{t('pages.clients.active')}</SelectItem>
              <SelectItem value="inactive">{t('pages.clients.inactive')}</SelectItem>
            </SelectContent>
          </Select>
          <ListGridToggle value={view} onChange={setView} testIdPrefix="clients-view" />
        </Card>

        {loading ? (
          <p className="text-sm text-muted-foreground" data-testid="clients-loading">{t('common.loading', { defaultValue: 'Loading…' })}</p>
        ) : view === 'grid' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sorted.map((c) => {
              const initials = c.logo || (c.name || '??').slice(0, 2).toUpperCase();
              const suiteCodes = c.suite_codes || [];
              const renewal = c.contract_end || c.contractEnd;
              return (
                <Card key={c.id} className="p-5 shadow-card hover:shadow-medium transition-smooth flex flex-col" data-testid={`client-card-${c.id}`}>
                  <div className="flex items-start gap-4">
                    {c.logo_url ? (
                      <div className="h-[84px] w-[84px] rounded-xl bg-white border border-border/60 flex items-center justify-center shrink-0 shadow-soft overflow-hidden p-1.5" data-testid={`client-logo-${c.id}`}>
                        <img src={c.logo_url} alt={c.name} className="h-full w-full object-contain" />
                      </div>
                    ) : (
                      <div className="h-[84px] w-[84px] rounded-xl bg-gradient-primary text-white flex items-center justify-center font-bold text-2xl shrink-0 shadow-soft">{initials}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={(c.status || 'active') === 'active' ? 'default' : 'outline'}>{t(`common.statusValues.${c.status || 'active'}`, { defaultValue: c.status || 'active' })}</Badge>
                        {c.plan && <Badge variant="outline" className="text-xs uppercase tracking-wider font-semibold">{c.plan}</Badge>}
                      </div>
                      <h3 className="font-display font-bold text-base tracking-tight mt-2 leading-snug break-words flex items-start gap-1.5" title={c.name}>{c.name}<GovernanceDot c={c} /></h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.sector || '—'} · {c.country || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-2 mt-5 pt-4 border-t border-border/60">
                    <div className="min-w-0" title={t('phase3.clients.collaboratorsHint', { defaultValue: 'Canonical active collaborator population registered in CTH (excludes candidates and external participants).' })} data-testid={`client-collaborators-${c.id}`}><p className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight break-words">{t('phase3.clients.collaborators', { defaultValue: 'Collaborators' })}</p><p className="text-sm font-bold mt-0.5 tabular-nums">{c.collaborators_count ?? '—'}</p></div>
                    <div className="min-w-0"><p className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight break-words">{t('pages.clients.suites')}</p><p className="text-sm font-bold mt-0.5 tabular-nums">{suiteCodes.length}</p></div>
                    <div className="min-w-0"><p className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight break-words">{t('pages.clients.userLimit', { defaultValue: 'Max users' })}</p><p className="text-sm font-bold mt-0.5" data-testid={`client-grid-tier-${c.id}`}>{tierLabel(c.user_limit_tier)}</p></div>
                  </div>
                  <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                    {c.contact_email && <div className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{c.contact_email}</div>}
                    {c.contact_phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 shrink-0" />{c.contact_phone}</div>}
                  </div>
                  <div className="mt-auto pt-4 flex items-center gap-2">
                    <Button
                      variant="outline" size="sm" className="flex-1"
                      onClick={() => navigate(`/admin/clients/${c.id}`)}
                      data-testid={`client-view-${c.id}`}
                    >
                      <Building2 className="h-3.5 w-3.5 mr-1.5" />{t('pages.clients.view')}
                    </Button>
                    <Button
                      variant="outline" size="sm" className="flex-1"
                      onClick={() => openManage(c)}
                      data-testid={`client-manage-suites-${c.id}`}
                    >
                      <Settings2 className="h-3.5 w-3.5 mr-1.5" />{t('pages.clients.manageSuites')}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`client-actions-${c.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isAdmin && (
                          <>
                            <DropdownMenuItem onClick={() => navigate(`/admin/clients/${c.id}`)} data-testid={`client-edit-${c.id}`}>
                              <Pencil className="h-3.5 w-3.5 mr-2" />{t('pages.clients.edit', { defaultValue: 'Edit client' })}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleClientStatus(c)} data-testid={`client-toggle-status-${c.id}`}>
                              <Power className="h-3.5 w-3.5 mr-2" />
                              {(c.status || 'active') === 'active'
                                ? t('pages.clients.deactivate', { defaultValue: 'Deactivate' })
                                : t('pages.clients.activate', { defaultValue: 'Activate' })}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem onClick={() => setArchiveTarget(c)} data-testid={`client-archive-${c.id}`}>
                          <Archive className="h-3.5 w-3.5 mr-2" />{t('governance.archiveClient', { defaultValue: 'Archive client' })}
                        </DropdownMenuItem>
                        {isAdmin && (
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(c)} data-testid={`client-delete-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" />{t('pages.clients.delete', { defaultValue: 'Delete permanently' })}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </Card>
              );
            })}
            {!sorted.length && (
              <p className="text-sm text-muted-foreground italic col-span-full">{t('clientProfile.noClients', { defaultValue: 'No clients match the filters.' })}</p>
            )}
          </div>
        ) : (
          <Card className="shadow-card overflow-x-auto" data-testid="clients-list">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-5 py-3"><SortableHeader sort={sort} k="name">{t('pages.clients.client', { defaultValue: 'Client' })}</SortableHeader></th>
                  <th className="px-5 py-3 hidden md:table-cell"><SortableHeader sort={sort} k="industry">{t('common.industry')}</SortableHeader></th>
                  <th className="px-5 py-3 hidden lg:table-cell text-right">{t('phase3.clients.collaborators', { defaultValue: 'Collaborators' })}</th>
                  <th className="px-5 py-3 hidden md:table-cell text-right"><SortableHeader sort={sort} k="suites">{t('common.suites')}</SortableHeader></th>
                  <th className="px-5 py-3 hidden lg:table-cell"><SortableHeader sort={sort} k="tier">{t('pages.clients.userLimit', { defaultValue: 'Max users' })}</SortableHeader></th>
                  <th className="px-5 py-3"><SortableHeader sort={sort} k="status">{t('common.status')}</SortableHeader></th>
                  <th className="px-5 py-3 w-40"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sorted.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/30" data-testid={`client-row-${c.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {c.logo_url ? (
                          <div className="h-10 w-10 rounded-lg bg-white border border-border/60 flex items-center justify-center shrink-0 overflow-hidden p-0.5" data-testid={`client-logo-row-${c.id}`}>
                            <img src={c.logo_url} alt={c.name} className="h-full w-full object-contain" />
                          </div>
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gradient-primary text-white flex items-center justify-center font-bold text-xs shrink-0">{(c.name || '??').slice(0, 2).toUpperCase()}</div>
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold truncate flex items-center gap-1.5">{c.name}<GovernanceDot c={c} /></p>
                          <p className="text-xs text-muted-foreground truncate">{c.country || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-muted-foreground">{c.sector || '—'}</td>
                    <td className="px-5 py-3 hidden lg:table-cell text-right tabular-nums" data-testid={`client-row-collaborators-${c.id}`}>{c.collaborators_count ?? '—'}</td>
                    <td className="px-5 py-3 hidden md:table-cell text-right tabular-nums">{(c.suite_codes || []).length}</td>
                    <td className="px-5 py-3 hidden lg:table-cell"><Badge variant="outline" className="text-xs" data-testid={`client-tier-${c.id}`}>{tierLabel(c.user_limit_tier)}</Badge></td>
                    <td className="px-5 py-3"><Badge variant={(c.status || 'active') === 'active' ? 'default' : 'outline'}>{t(`common.statusValues.${c.status || 'active'}`, { defaultValue: c.status || 'active' })}</Badge></td>
                    <td className="px-5 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/clients/${c.id}`)} data-testid={`client-view-row-${c.id}`}>
                        {t('pages.clients.view')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openManage(c)} data-testid={`client-manage-suites-row-${c.id}`}>
                        {t('pages.clients.manageSuites')}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`client-actions-row-${c.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isAdmin && (
                            <>
                              <DropdownMenuItem onClick={() => navigate(`/admin/clients/${c.id}`)} data-testid={`client-edit-row-${c.id}`}>
                                <Pencil className="h-3.5 w-3.5 mr-2" />{t('pages.clients.edit', { defaultValue: 'Edit client' })}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toggleClientStatus(c)} data-testid={`client-toggle-status-row-${c.id}`}>
                                <Power className="h-3.5 w-3.5 mr-2" />
                                {(c.status || 'active') === 'active'
                                  ? t('pages.clients.deactivate', { defaultValue: 'Deactivate' })
                                  : t('pages.clients.activate', { defaultValue: 'Activate' })}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          <DropdownMenuItem onClick={() => setArchiveTarget(c)} data-testid={`client-archive-row-${c.id}`}>
                            <Archive className="h-3.5 w-3.5 mr-2" />{t('governance.archiveClient', { defaultValue: 'Archive client' })}
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(c)} data-testid={`client-delete-row-${c.id}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-2" />{t('pages.clients.delete', { defaultValue: 'Delete permanently' })}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {!sorted.length && (
                  <tr><td colSpan={7} className="px-5 py-6 text-center text-muted-foreground italic">{t('clientProfile.noClients', { defaultValue: 'No clients match the filters.' })}</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {/* Manage Suites modal (Prompt 6) */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="manage-suites-dialog">
          <DialogHeader>
            <DialogTitle>{t('clientProfile.manageSuitesTitle', { defaultValue: 'Manage suites' })}</DialogTitle>
            <DialogDescription>
              {manageTarget?.name} — {t('clientProfile.manageSuitesDesc', { defaultValue: 'Check the suites this client is using. Changes save to the database immediately.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-2 py-2 max-h-[60vh] overflow-y-auto">
            {allSuites.map((s) => {
              const Icon = SUITE_ICONS[s.code] || Layers;
              const checked = draftSuites.includes(s.code);
              return (
                <label
                  key={s.code}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
                  data-testid={`suite-toggle-${s.code}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggleSuite(s.code)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-medium"><Icon className="h-3.5 w-3.5" />{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.tagline || s.description || ''}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageOpen(false)} data-testid="manage-suites-cancel">{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={saveSuites} disabled={saving} data-testid="manage-suites-save">{saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save', { defaultValue: 'Save' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BUG 01 + BUG 02 — New client wizard */}
      <NewClientDialog open={newOpen} onOpenChange={setNewOpen}
                       onCreated={() => reload()} />
      {/* Sesión B WF1 — advisor surface: client creation REQUEST */}
      <ClientRequestDialog open={clientReqOpen} onOpenChange={setClientReqOpen}
                           onSubmitted={() => { /* nothing to refresh here */ }} />
      {/* Sesión B WF2 — advisor surface: suite activation REQUEST */}
      <SuiteRequestDialog
        open={suiteReqOpen}
        onOpenChange={setSuiteReqOpen}
        company={manageTarget}
        requesterName={user?.name}
        activeSuites={manageTarget?.suite_codes || []}
      />

      {/* UAT Sprint 01 · Finding 4 — guarded client delete */}
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title={t('pages.clients.deleteConfirmTitle', { defaultValue: 'Delete client permanently?' })}
        description={t('pages.clients.deleteConfirmDesc', { defaultValue: 'This will delete the company and cascade its users, departments, assignments and intelligence actions. Type the company name to confirm.' })}
        expectedInput={deleteTarget?.name || ''}
        inputPlaceholder={t('pages.clients.deleteConfirmPh', { defaultValue: 'Type the company name to confirm' })}
        confirmLabel={t('pages.clients.delete', { defaultValue: 'Delete permanently' })}
        testIdPrefix="client-delete"
        onConfirm={deleteClient}
      />

      {/* Sprint Governance — reversible archive confirm */}
      <Dialog open={!!archiveTarget} onOpenChange={(v) => !v && setArchiveTarget(null)}>
        <DialogContent data-testid="client-archive-dialog">
          <DialogHeader>
            <DialogTitle>{t('governance.archiveConfirmTitle', { defaultValue: 'Archive this client?' })}</DialogTitle>
            <DialogDescription>
              {archiveTarget?.name} — {t('governance.archiveConfirmDesc', { defaultValue: 'The client is hidden from active views but nothing is deleted. All data, history and audit records are preserved, and an HM Admin can restore it from the Trash workspace at any time.' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)} disabled={archiving}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={archiveClient} disabled={archiving} data-testid="client-archive-confirm">
              {archiving ? t('common.saving', { defaultValue: 'Saving…' }) : t('governance.archiveClient', { defaultValue: 'Archive client' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
